import type {
  FloorPlannerComposite,
  FloorPlannerCompositeRef,
  FloorPlannerLayout,
} from '@repo/frontend-types';
import {
  asArray,
  entityNamesByRefid,
  fileableFloorBids,
  isFileableObserver,
  isLive,
  layoutCurrentDesigns,
  observerTargetRef,
} from '@repo/front-end-utils';

import { encodeRef } from './compositeRefOptions';
import type { LiveTwinConfiguration } from './useLiveTwinConfiguration';

/**
 * **Is this thing measured, and how?** — the one rule, for every surface that asks.
 *
 * ## Why there is one of these now
 *
 * There were three, and none of them could see a complex measure (epic
 * `complex-measures-in-configuration`, Scope Ledger rows 18, 81 and 82):
 *
 *   - the Observers stage collapsed any entity with one or more complete polygons to *configured*
 *     and everything else to *no observer*, with no composite input at all;
 *   - the Launch summary subtracted the number of distinct observer targets from
 *     `placedItems + surfaces` — arithmetic over two set sizes rather than a set operation, which
 *     goes NEGATIVE when an observer names an entity that has left the plan, which is why it was
 *     wrapped in `Math.max(0, …)`;
 *   - the composite editor asked its own question about inputs, and consulted `_deleted` but never
 *     `excluded`.
 *
 * Measured on the committed NP2-STN fixture (staging twin 522, configuration 5) through the real
 * matchers: 160 entities, 140 observed, 6 totalled by a measure and not otherwise watched, 14
 * genuinely uncovered. The old Launch arithmetic said **20**, and six of those twenty were already
 * being totalled.
 *
 * ## One level, deliberately
 *
 * An entity is `totalled` because a live measure points at it. Whether THAT measure's own inputs
 * are measured is a different question, and it has two existing answers already: the editor's
 * `input-not-measured` rule as the operator edits (`compositeGraph.ts`), and the server's refusal
 * at filing (`statement-file-plan.ts`). Recursive completeness — `FullyConfigured` — is the epic's
 * later `completeness-and-coverage` feature. Adding a second recursive rule here would be a third
 * answer to a question that already has two.
 *
 * ⚠ **That feature has now landed, and the recursive rule is {@link computeCompleteness} BELOW —
 * in this module, beside `computeCoverage`, over the same edge set.** The paragraph above is what
 * put it here rather than in a fourth place. `computeCoverage` itself is unchanged: it answers *is
 * this entity measured at all*, which the Observers stage and the Launch checklist both need, and
 * which is **not** the same question as *is this total finished*. One module owns coverage; two
 * exports answer two questions.
 *
 * ## Being an INPUT is not coverage
 *
 * Only a measure's **target** is covered by it. A zone that appears in the whole-gym total is not
 * thereby measured — that total is counting it, not watching it — which is exactly what
 * `input-not-measured` says from the other side.
 *
 * ## Keyed by the encoded reference, not by refid
 *
 * A measure can target the twin or a floor as well as an entity, and `encodeRef`
 * (`@repo/front-end-utils`, `twin-planner/compositeRefs.ts`) is this feature's single canonical
 * string form for all three. The
 * composite editor already keys its own counts that way. Everything here speaks that currency;
 * the two entity-level surfaces convert at their edges.
 */

/** What is measuring this thing, if anything. */
export type CoverageState = 'observed' | 'totalled' | 'uncovered';

/**
 * A complex measure as the coverage rule sees it — references already encoded.
 *
 * `excluded` is carried rather than filtered out at construction, so the exclusion decides
 * something in exactly one place ({@link computeCoverage}) and a later reader has one line to find.
 */
export interface MeasureEdge {
  refid: string;
  target: string;
  inputs: string[];
  /** *Exclude from processing* — the measure is not published, so it measures nothing. */
  excluded?: boolean;
  /**
   * *Also count its own cameras* — the target's own observers are members too.
   *
   * ⚠ **Carried because the server counts them and this rule must agree.** A total over ONLY its
   * target's cameras has an empty `inputs` list, and without this flag it read as an empty draft
   * here while the server answered finished — so the Launch screen miscounted it and its parent
   * was tagged over a total that was correct. `UGG-FIN-Z011` on Finsbury Park is that shape.
   */
  includesOwnObservers?: boolean;
}

/**
 * A measure that adds up nothing — no inputs, and not counting its own cameras either
 * (`launch-composite-refusals`, R1).
 *
 * ⚠ **Narrower than "not finished", deliberately.** `computeCompleteness` also calls a total over
 * only its own cameras unfinished when the target HAS no cameras (`ownMembers` is 0 there), but that
 * total has said what it adds up and is waiting on the plan — it is not empty. Launch lists the two
 * separately, so this predicate must not absorb the second case.
 */
export const isEmptyMeasure = (measure: MeasureEdge): boolean =>
  measure.inputs.length === 0 && measure.includesOwnObservers !== true;

/** One thing on the plan a measure can point at, with everything the surfaces need to render it. */
export interface PlanEntity {
  refid: string;
  kind: 'surface' | 'item';
  /** Display name — `entityNamesByRefid`, the same resolution the composite picker uses. */
  name: string;
  /** `encodeRef({ kind: 'entity', refid, entityKind: kind })`. */
  ref: string;
}

export interface CoverageSummary {
  total: number;
  observed: number;
  totalled: number;
  uncovered: number;
  /** Entities carrying two or more observers — a partial group once the plan is filed. */
  partialGroups: number;
  /** Every uncovered entity, in plan reading order. */
  uncoveredEntities: PlanEntity[];
  /**
   * Every entity with no observer of its OWN — the uncovered ones and the ones only a composite
   * measure totals — in plan reading order. The Launch *Observers* block's list; its count is this
   * list's length, so the number and the names cannot disagree (`launch-summary-detail`, R4).
   */
  unobservedEntities: PlanEntity[];
}

/**
 * The plan's measures as edges.
 *
 * `isLive` drops tombstones, exactly as the stage's own reader does; `excluded` travels.
 */
export const planMeasures = (
  composites: FloorPlannerComposite[] | null | undefined,
): MeasureEdge[] => {
  const edges: MeasureEdge[] = [];
  for (const composite of asArray(composites).filter(isLive)) {
    const target = encodeRef(composite.target);
    // An unencodable target is a malformed row, which `encodeRef` documents at length. It cannot
    // measure anything, and admitting it would let two different malformed rows read as one.
    if (!target) continue;
    edges.push({
      refid: composite.refid,
      target,
      inputs: asArray(composite.inputs).map(encodeRef),
      excluded: composite.excluded,
      includesOwnObservers: composite.includesOwnObservers,
    });
  }
  return edges;
};

/**
 * Everything on this plan a measure can point at, **in plan reading order** — surfaces then items,
 * per current design, which is the order the Observers stage already walks and the order the
 * Launch block's name list is asserted in. It is NOT refid order.
 */
export const planEntities = (layout: FloorPlannerLayout | null | undefined): PlanEntity[] => {
  const names = entityNamesByRefid(layout ?? null);
  const entities: PlanEntity[] = [];
  const push = (refid: string, kind: 'surface' | 'item') => {
    if (typeof refid !== 'string' || !refid) return;
    entities.push({
      refid,
      kind,
      /*
       * ⚠ The generic label, never the refid. `entityNamesByRefid` walks the same entity set with
       * the same guards and always answers something, so this branch is not reachable today — but
       * the refid is an internal identifier and FR6 says a name is never one, so the fallback
       * matches `surfaceDisplayName` / `itemDisplayName` rather than leaking a code onto the Launch
       * screen if that ever stops being true.
       */
      name: names.get(refid) ?? (kind === 'surface' ? 'Zone' : 'Equipment'),
      ref: encodeRef({ kind: 'entity', refid, entityKind: kind }),
    });
  };
  for (const design of layoutCurrentDesigns(layout ?? null)) {
    for (const surface of asArray(design?.surfaces).filter(isLive)) push(surface.refid, 'surface');
    for (const item of asArray(design?.items).filter(isLive)) push(item.refid, 'item');
  }
  return entities;
};

/**
 * How many cameras on one target make the filing derive a total over them (a partial group) — the
 * server's pass 3 (`statement-file-plan.ts`, "the derived partial groups", `group.length < 2`).
 * One constant, so the coverage summary and the composite stage's automatic rows cannot disagree
 * about which targets have one.
 */
export const AUTOMATIC_TOTAL_MIN_CAMERAS = 2;

export interface AutomaticTotal {
  /** The target as a plan reference — a floor, or a zone or machine. */
  target: FloorPlannerCompositeRef;
  /** How many cameras it adds up. */
  cameras: number;
}

/**
 * Every total the filing will DERIVE from this plan's observers: one per target watched by
 * {@link AUTOMATIC_TOTAL_MIN_CAMERAS} or more cameras that would be filed (a region and a stream).
 *
 * `refiling-preserves-configuration` (epic ledger row 123): the read folds these away and the filing
 * rebuilds them, so no statement lists them — and the composite stage showed one total of PUL-CON's
 * fifteen. This is what lets it show the other fourteen. Pure over the SAVED plan, in plan reading
 * order: floors first, then each floor's surfaces and items.
 */
export const automaticTotals = (
  layout: FloorPlannerLayout | null | undefined,
): AutomaticTotal[] => {
  const counts = new Map<string, { target: FloorPlannerCompositeRef; cameras: number }>();
  // Fileability depends on the floor's NAME alone (named, not sharing a folded name), so any
  // non-blank prefix answers it; the BIDs themselves are not used here.
  const namedFloorIds = new Set(fileableFloorBids(layout, '_').keys());
  for (const design of layoutCurrentDesigns(layout ?? null)) {
    for (const observer of asArray(design?.observers).filter(isLive)) {
      if (!isFileableObserver(observer)) continue;
      const target = observerTargetRef(observer);
      // A floor with no fileable identity (unnamed, or ambiguous) files nothing — no total either.
      if (target?.kind === 'floor' && !namedFloorIds.has(target.floorId)) continue;
      const ref = encodeRef(target);
      if (!target || !ref) continue;
      const entry = counts.get(ref) ?? { target, cameras: 0 };
      entry.cameras += 1;
      counts.set(ref, entry);
    }
  }
  const totals = Array.from(counts.values()).filter(t => t.cameras >= AUTOMATIC_TOTAL_MIN_CAMERAS);
  // Floors first — the order the stage reads a gym in, top down.
  return [
    ...totals.filter(t => t.target.kind === 'floor'),
    ...totals.filter(t => t.target.kind !== 'floor'),
  ];
};

/**
 * How many observers the plan holds against each entity, keyed by encoded reference.
 *
 * This is the SAVED plan's answer — what Go Live would file. The Observers stage builds its own
 * from the operator's in-session polygons instead, which is why the counts are a parameter to
 * {@link computeCoverage} rather than something it reads for itself: the stage must reflect a
 * polygon drawn thirty seconds ago and not yet saved.
 */
export const observerCountsFromLayout = (
  layout: FloorPlannerLayout | null | undefined,
): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const design of layoutCurrentDesigns(layout ?? null)) {
    for (const observer of asArray(design?.observers).filter(isLive)) {
      /*
       * ⚠ A floor-wide observer counts against its FLOOR (`refiling-preserves-configuration`), in the
       * reference shape a floor total's inputs already use — so a total whose member is that floor
       * does not warn that the floor is unmeasured while cameras watch it.
       */
      const ref = encodeRef(observerTargetRef(observer));
      if (!ref) continue;
      counts.set(ref, (counts.get(ref) ?? 0) + 1);
    }
  }
  return counts;
};

/**
 * The rule.
 *
 * `measures` is the set as it WOULD be, not necessarily as the plan holds it — the composite
 * editor passes the measure currently in its form alongside the rest, so the answer describes the
 * plan the operator is about to have.
 *
 * Order matters: an entity with both an observer and a measure on it is `observed`. The measure is
 * still there and still totals it; what the surfaces need to know is that this entity is watched
 * on its own account.
 *
 * ⚠ **The returned map is SPARSE** — it keys only what something measures. A reference nothing
 * watches and nothing totals has **no entry**, not an `uncovered` one, so a caller asking about an
 * arbitrary reference reads an absent value as `uncovered` rather than testing equality against it.
 * {@link summarisePlanCoverage} does this for plan entities; `findUnmeasuredInputs` does it for an
 * input that may be anything on the plan.
 */
export const computeCoverage = ({
  observerCountsByRef,
  measures,
}: {
  observerCountsByRef: ReadonlyMap<string, number>;
  measures: ReadonlyArray<MeasureEdge>;
}): Map<string, CoverageState> => {
  /*
   * ⚠ An EXCLUDED measure measures nothing, and this is the one place that decides it.
   *
   * *Exclude from processing* means the measure is not published, so its target gets no number
   * from it. Treating it as coverage would report an entity as measured on the strength of a total
   * nobody computes — which is the same class of false claim as reporting a zone as unmeasured
   * when a measure already totals it, arrived at from the other direction.
   */
  const measuredTargets = new Set<string>();
  for (const measure of measures) {
    if (measure.excluded) continue;
    measuredTargets.add(measure.target);
  }

  const coverage = new Map<string, CoverageState>();
  const classify = (ref: string): CoverageState => {
    if ((observerCountsByRef.get(ref) ?? 0) > 0) return 'observed';
    if (measuredTargets.has(ref)) return 'totalled';
    return 'uncovered';
  };

  // `.forEach` rather than `for…of` over the iterators: this app compiles to ES5 and does not set
  // `downlevelIteration`, so iterating a Map or a Set directly is a compile error here.
  observerCountsByRef.forEach((_count, ref) => coverage.set(ref, classify(ref)));
  measuredTargets.forEach(ref => {
    if (!coverage.has(ref)) coverage.set(ref, classify(ref));
  });
  return coverage;
};

/** Why one of a measure's inputs does not count towards it being finished. */
export type UnsatisfiedReason = 'no-source' | 'unfinished-total' | 'excluded-total';

/** One measure's verdict, and the inputs that stopped it being finished. */
export interface CompletenessVerdict {
  fullyConfigured: boolean;
  /**
   * Does it add up anything that is measured, all the way down? A total goes live if so — summing
   * the sources that are — and only one with nothing measured is held back
   * (`onboarding-polish-partial-composites`). The server's `computeMeasuredSources` answers the same
   * question; the shared case table's `expectMeasured` pins the two together.
   *
   * ⚠ Computed, never derived from `unsatisfied`: a cycle returns an empty list, so a count taken
   * by subtraction would call it measured.
   */
  measured: boolean;
  /** In the measure's own input order, so a caller can name them as the operator sees them. */
  unsatisfied: { ref: string; reason: UnsatisfiedReason }[];
}

/**
 * **Is this total finished?** — the recursive rule, over the same edge set `computeCoverage` uses.
 *
 * ## Why this is not `computeCoverage` with a flag
 *
 * They answer different questions. `computeCoverage` asks *is this entity measured at all*, one
 * level, and the Observers stage and the Launch checklist both read it. This asks *does everything
 * this total adds up actually resolve, all the way down* — and the answer for a parent depends on
 * the answer for its children.
 *
 * ## ⛔ The recursion is the point
 *
 * If one section at the bottom has no camera, every total above it is short of what its name
 * claims, so every one of them reads *Not finished*. Whether each goes live is the second answer in
 * the verdict, `measured`: an unfinished total that adds up anything measured is sent with the
 * sources it has (`onboarding-polish-partial-composites`).
 *
 * ## ⚠ A measure on an input takes precedence over that input's own cameras
 *
 * An entity that has a camera AND a total named for it contributes through the TOTAL, because that
 * is the member the server plans. So if the total is unfinished, the parent is short even though
 * the entity is watched. The obvious rule — *has a camera, therefore satisfied* — answers `true`
 * and is wrong, which is why the shared fixture carries that case explicitly.
 *
 * ## ⚠ Pinned to the server's implementation
 *
 * `packages/frontend-types/src/twinsense/__fixtures__/completeness-cases.json` is a table of
 * measure graphs and their expected verdicts, asserted by this rule and by the middleware's
 * `composite-completeness.ts`. A case answered differently by the two turns one of them red.
 *
 * ## ⚠ The returned map is TOTAL, not sparse
 *
 * Unlike `computeCoverage`'s — every measure has an entry, keyed by its `refid`. The two maps are
 * keyed differently and mean different things; do not read one as the other.
 */
export const computeCompleteness = ({
  observerCountsByRef,
  measures,
}: {
  observerCountsByRef: ReadonlyMap<string, number>;
  measures: ReadonlyArray<MeasureEdge>;
}): Map<string, CompletenessVerdict> => {
  /*
   * ⚠ An EXCLUDED measure satisfies nothing — the same ruling `computeCoverage` makes at its own
   * exclusion test, inherited rather than re-decided. It is published nowhere and computes nothing,
   * so it gives its target no source. Being excluded does not make it UNFINISHED, though: that is a
   * different question and it is still answered on the measure's own merits.
   */
  const measureByTarget = new Map<string, MeasureEdge>();
  measures.forEach(measure => {
    if (measure.excluded) return;
    if (!measureByTarget.has(measure.target)) measureByTarget.set(measure.target, measure);
  });

  /*
   * ⛔ **Every target, excluded ones INCLUDED — and this set is the whole reason the two rules
   * agreed only by luck before.** The server's planner turns an input into a `composite_measure`
   * member whenever ANY measure targets it (`inputIsItselfATotal = targets.has(...)`), then answers
   * false because that child is excluded. Reading `measureByTarget` alone, this rule fell through
   * to the entity's own camera count instead and answered FINISHED — so an entity with a camera and
   * an excluded total named for it read as satisfied here and as unfinished there, with nothing on
   * screen saying so.
   */
  const targetsOfAnyMeasure = new Set<string>();
  measures.forEach(measure => targetsOfAnyMeasure.add(measure.target));

  const settled = new Map<string, CompletenessVerdict>();
  const onStack = new Set<string>();
  /*
   * ⚠ **A verdict reached through a cycle cut is not memoised, anywhere on the path** — the same
   * rule as the server's `computeMeasuredSources`. `measured` is an "any member" answer, so inside
   * a cycle the node reached second would otherwise answer from the cut and the result would
   * depend on walk order, which differs between the two sides. Cycles are refused (`findCycle`), so
   * this is a guard, not a hot path; `fullyConfigured` is unaffected either way.
   */
  let cutHit = false;

  const verdictFor = (measure: MeasureEdge): CompletenessVerdict => {
    const memo = settled.get(measure.refid);
    if (memo !== undefined) return memo;

    // ⛔ A cycle. Refused elsewhere (`findCycle`), so reaching one means a guard failed — and a
    //    measure whose completeness cannot be established is not established as complete.
    if (onStack.has(measure.refid)) {
      cutHit = true;
      return { fullyConfigured: false, measured: false, unsatisfied: [] };
    }

    onStack.add(measure.refid);
    const outerCut = cutHit;
    cutHit = false;

    const unsatisfied: CompletenessVerdict['unsatisfied'] = [];
    // Any input that resolves to something measured — set beside the completeness walk, over the
    // same classification, so the two answers cannot classify an input differently.
    let measuredInput = false;
    measure.inputs.forEach(ref => {
      // The target among its own inputs is expressed by `includesOwnObservers`, never as a member.
      if (ref === measure.target) return;

      const totalling = measureByTarget.get(ref);
      if (totalling) {
        // ⚠ The total wins over the input's own cameras — see the note above.
        const child = verdictFor(totalling);
        if (!child.fullyConfigured) {
          unsatisfied.push({ ref, reason: 'unfinished-total' });
        }
        // A partly measured child goes live with what it has, so it gives its parent a source.
        if (child.measured) measuredInput = true;
        return;
      }
      /*
       * ⛔ **An excluded total CLAIMS its target on the server, so the target's own cameras do not
       * rescue it** — the planner makes the member a `composite_measure` whenever ANY measure names
       * that target, then answers false because the child is excluded.
       *
       * ⚠ **Only where the entity HAS cameras, though, and the narrowness is the point.** With no
       * cameras the answer was already `no-source` and both sides agreed; `no-source` is the
       * reason that drives the SAVE-BLOCKING error, so reclassifying it here would quietly demote
       * a refusal to a warning. Measured: it turned `findUnmeasuredInputs` red on a case that has
       * nothing to do with this divergence.
       */
      /*
       * ⛔ **THE EXCLUDED TOTAL IS NAMED FIRST, WHATEVER THE CAMERA COUNT — and this was narrower
       * for a reason that has since gone away.** It used to check cameras first, so an input with
       * no cameras whose only total is excluded fell through to `no-source` and the operator was
       * told it "has no camera and no total of its own" — which is half false: it HAS a total, and
       * the total is switched off. The narrowness was there to avoid demoting a Save-blocking
       * refusal to a warning; `no-source` is warning-only now, so there is no refusal left to
       * demote and the accurate reason is simply better. Raised by automated review on PR #1744.
       */
      if (targetsOfAnyMeasure.has(ref)) {
        unsatisfied.push({ ref, reason: 'excluded-total' });
        return;
      }
      if ((observerCountsByRef.get(ref) ?? 0) > 0) {
        measuredInput = true;
        return;
      }
      unsatisfied.push({ ref, reason: 'no-source' });
    });

    onStack.delete(measure.refid);

    /*
     * ⛔ **An empty total is NOT finished, and it is the one state that must never read as a
     * measured zero.** It is a valid draft — the operator has named what they are going to total
     * and not yet said what goes into it — so it saves and files; it just does not claim to
     * measure anything.
     */
    /*
     * ⚠ **Its own cameras are members too.** The server plans them as such, so a total carrying
     * only *Also count its own cameras* is finished there — and must be finished here.
     */
    const ownMembers = measure.includesOwnObservers
      ? (observerCountsByRef.get(measure.target) ?? 0)
      : 0;

    // Wider than `isEmptyMeasure` on purpose: an own-cameras-only total whose target has no cameras
    // is unfinished here, but not EMPTY — it has said what it adds up. Keep the two distinct.
    const answer: CompletenessVerdict = {
      fullyConfigured: unsatisfied.length === 0 && measure.inputs.length + ownMembers > 0,
      measured: measuredInput || ownMembers > 0,
      unsatisfied,
    };
    if (!cutHit) settled.set(measure.refid, answer);
    cutHit = outerCut || cutHit;
    return answer;
  };

  const verdicts = new Map<string, CompletenessVerdict>();
  measures.forEach(measure => {
    cutHit = false;
    verdicts.set(measure.refid, verdictFor(measure));
  });
  return verdicts;
};

/**
 * The plan's coverage, counted over its entities.
 *
 * ⚠ **Counted over `planEntities`, never over the coverage map.** The map also holds references
 * that are not plan entities — the twin, a floor, an observer naming an entity that has left the
 * plan (one such exists on the NP2-STN fixture) — and counting those would report coverage for
 * things nobody can see on a plan.
 */
export const summarisePlanCoverage = ({
  layout,
  observerCountsByRef,
  measures,
}: {
  layout: FloorPlannerLayout | null | undefined;
  observerCountsByRef: ReadonlyMap<string, number>;
  measures: ReadonlyArray<MeasureEdge>;
}): CoverageSummary => {
  const entities = planEntities(layout);
  const coverage = computeCoverage({ observerCountsByRef, measures });

  let observed = 0;
  let totalled = 0;
  let partialGroups = 0;
  const uncoveredEntities: PlanEntity[] = [];
  const unobservedEntities: PlanEntity[] = [];

  for (const entity of entities) {
    const state = coverage.get(entity.ref) ?? 'uncovered';
    if (state !== 'observed') unobservedEntities.push(entity);
    if (state === 'observed') {
      observed += 1;
      if ((observerCountsByRef.get(entity.ref) ?? 0) >= AUTOMATIC_TOTAL_MIN_CAMERAS) {
        partialGroups += 1;
      }
    } else if (state === 'totalled') {
      totalled += 1;
    } else {
      uncoveredEntities.push(entity);
    }
  }

  return {
    total: entities.length,
    observed,
    totalled,
    uncovered: uncoveredEntities.length,
    partialGroups,
    uncoveredEntities,
    unobservedEntities,
  };
};

/**
 * What the Launch block renders, derived rather than assembled in JSX.
 *
 * ⚠ **This exists because the arithmetic around the rule is where the defect being deleted lived.**
 * The old Launch summary's bug was not in any rule — it was in a subtraction and a clamp written
 * inline in the page, which nothing could reach from a test. Putting the read translation back in
 * the same place would rebuild exactly that: a review measured it, and setting the (since removed)
 * name cap to 3 or `withoutObserver` to 0 left all 1,271 tests green.
 *
 * **Every name, uncapped** (`launch-summary-detail`, R5). The names used to sit inline in a
 * sentence, capped at ten with an "and N more"; they now sit in a closed-by-default list, where a
 * cap has no job and a slice would present part of the list as the whole.
 */
export interface CoverageBlock {
  /** The Launch summary's vocabulary (`RegisterReadState`), not the stage's. */
  read: 'loading' | 'ready' | 'error' | 'no-plan';
  total: number;
  observed: number;
  totalled: number;
  uncovered: number;
  /**
   * Every uncovered entity, in plan reading order.
   *
   * ⚠ **The refid travels with the name, and it is not decoration.** A plan's display names are
   * NOT unique — `compositeRefOptions` says so at length, and `entityNamesByRefid` answers `Zone`
   * or `Equipment` for anything unnamed — so a React list keyed on the name alone reuses rows
   * between two entities called the same thing. The refid is the only unique thing here.
   */
  names: { refid: string; name: string }[];
  /**
   * Complex measures on this plan that are not finished but add up something measured — one or
   * more of the things they add up resolves to nothing, or to a total that is itself unfinished.
   *
   * ⚠ **A count and its names, never a gate** — `refused` below is the only gate here. These
   * totals go live adding up the sources that are measured, so they read low until they are
   * finished (`onboarding-polish-partial-composites`); they are marked in the database.
   */
  unfinished: number;
  /** Every one of them, as the operator reads them. */
  unfinishedNames: { refid: string; name: string }[];
  /**
   * Not finished and adding up nothing measured, so held back from the live system. Never a gate;
   * not ALSO in `unfinishedNames` or `empty` — each measure is listed once.
   */
  nothingMeasuredNames: { refid: string; name: string }[];
  /**
   * Every composite measure the twin would refuse, named for what it totals, with the Composite
   * measures step's own sentences for it (`composites-page-launch-refusals`, R5).
   *
   * ⛔ **THE ONE GATE IN THIS BLOCK.** Go Live is disabled while this is non-empty. It is read from
   * the layout Go Live files, so it holds in EVERY read state — unlike the lists above, which wait
   * for the twin's own configuration to land on the plan.
   */
  refused: { refid: string; name: string; reasons: string[] }[];
  /**
   * Whether the refusal check above ran. `'failed'` means it threw — a defect, since it does no
   * I/O — and Launch says it could not check rather than that nothing is refused (spec R6a).
   */
  refusalCheck: 'ok' | 'failed';
  /**
   * Measures that add up nothing (R3). Filed as drafts; never a gate. Not ALSO in
   * `unfinishedNames` — each measure is listed once.
   */
  empty: { refid: string; name: string }[];
  /**
   * Generated totals a person should look at (`complex-measures-in-configuration-parent-space-
   * composites`, FR11a). Never a gate; listed after the groups above, each measure once.
   */
  review?: { refid: string; name: string }[];
}

export const coverageBlock = ({
  summary,
  readState,
  plan,
  unfinished = [],
  nothingMeasured = [],
  refused = [],
  refusalCheck = 'ok',
  empty = [],
  review = [],
}: {
  summary: CoverageSummary;
  /** From {@link liveReadState} — how the twin's own configuration read went. */
  readState: 'pending' | 'ready' | 'failed';
  /**
   * The OTHER half of knowable, and not covered by the configuration read.
   *
   * `absent` is a terminal answer, not a kind of loading: a twin may simply have no saved plan, and
   * the plan read swallows its own error and yields null, so "still reading" there would spin for
   * ever on the one path where nothing is coming — the same trap {@link liveReadState} closes for a
   * failed configuration read.
   */
  plan: 'loading' | 'absent' | 'present';
  /**
   * The unfinished totals, already named and in plan order — the caller resolves them, because
   * naming a measure means naming what it is the total OF and that is a plan lookup.
   *
   * Absent means nothing is unfinished, which is also what an empty array means; both render the
   * line not at all rather than as a zero.
   */
  unfinished?: { refid: string; name: string }[];
  /** Not-finished totals with nothing measured, already named. Absent means none. */
  nothingMeasured?: { refid: string; name: string }[];
  /** Totals the twin would refuse, already named, with their reasons. Absent means none. */
  refused?: { refid: string; name: string; reasons: string[] }[];
  /** Whether the refusal check ran. Absent means it did. */
  refusalCheck?: 'ok' | 'failed';
  /** Empty measures, already named. Absent means none. */
  empty?: { refid: string; name: string }[];
  /** Generated totals needing a person's look (FR11a). Never a gate. */
  review?: { refid: string; name: string }[];
}): CoverageBlock => ({
  /*
   * One rule, two vocabularies. The stage says pending/ready/failed; this block's siblings speak
   * `RegisterReadState`, and `assets.read` beside it already does. Translated once, here, rather
   * than as a nested ternary in the page's JSX where no test could see it.
   */
  read:
    plan === 'loading'
      ? 'loading'
      : plan === 'absent'
        ? 'no-plan'
        : readState === 'pending'
          ? 'loading'
          : readState === 'failed'
            ? 'error'
            : 'ready',
  total: summary.total,
  observed: summary.observed,
  totalled: summary.totalled,
  uncovered: summary.uncovered,
  names: summary.uncoveredEntities.map(e => ({ refid: e.refid, name: e.name })),
  unfinished: unfinished.length,
  unfinishedNames: unfinished,
  nothingMeasuredNames: nothingMeasured,
  refused,
  refusalCheck,
  empty,
  review,
});

/**
 * Has the twin's live configuration been PLACED onto the plan yet?
 *
 * ⚠ **A settled read is not a placed plan, and coverage is answered from the plan.** The reads land
 * in state; a LATER effect (`useLiveTwinConfiguration`'s match) is what writes the twin's observers
 * and complex measures onto it. Between those two commits the reads say ready while the plan holds
 * none of them — so a coverage count taken there reports every entity as untracked, and the Launch
 * block's live region announces that wrong sentence before the right one replaces it.
 * `compositeMatch` is `null` until a match has run, which is exactly the signal.
 *
 * Deliberately NOT folded into {@link liveReadState}: that answers "did the observers read land?",
 * which the Observers stage asks for its own row counts and which is true before the placement is.
 * Only the coverage COUNT needs to wait for the plan.
 *
 * A twin that has never been live has no match coming and its plan is the whole truth, so it is
 * placed by definition.
 */
export const liveConfigurationPlaced = (
  live: LiveTwinConfiguration | null | undefined,
): boolean => {
  if (live == null) return true;
  /*
   * ⚠ Optional, and not defensively. This reads a hook's return value, and the wizard's own test
   * suites mock that hook with the two or three fields each one cares about — so a required read
   * here throws inside a render and takes every case in the file with it. It did: 98 of them, in
   * `handleGoLive.test.tsx`, which renders the whole page per case.
   *
   * The semantics are right either way: no statement means no placement is coming, so there is
   * nothing to wait for.
   */
  if (live.statement?.status !== 'ready') return true;
  return live.compositeMatch != null;
};

/**
 * Whether the twin's own configuration has landed — the one answer both the Observers stage and
 * the Launch summary read, so they cannot disagree about whether coverage is knowable yet.
 *
 * ⚠ **Not simply `observers.status === 'loading'`.** A failed CONFIGURATION read leaves the
 * observer read permanently at `loading`, because there is no configuration id to read against —
 * so that alone would report *pending* for ever on the one path where the answer is never coming.
 * It is `failed`, and each surface says so in its own words.
 *
 * ⚠ **The streams read is part of `pending`.** The match waits for the streams before it writes
 * anything into the plan, so until they land the plan holds no live observers and no live
 * measures, and every entity would read as uncovered. A failed streams read is NOT a failure here,
 * though — a stream link is not a measurement, and that failure has its own banner.
 *
 * `null` (no live configuration at all — a twin being created from scratch) is `ready`: the plan
 * is then the whole truth about what is measured.
 */
export const liveReadState = (
  live: LiveTwinConfiguration | null | undefined,
): 'pending' | 'ready' | 'failed' => {
  if (live == null) return 'ready';
  /*
   * The order is `OnboardingObservers`'s, verbatim, and it is not interchangeable with the obvious
   * one. A failed OBSERVERS read while the streams are still loading reports `pending` here,
   * because that is what the stage does today and this lift must not change the stage's behaviour
   * on the way past. Testing `failed` first would flip it.
   */
  const countsLoading =
    live.config.status !== 'failed' &&
    (live.observers.status === 'loading' || live.streams.status === 'loading');
  if (countsLoading) return 'pending';
  if (live.observers.status === 'failed' || live.config.status === 'failed') return 'failed';
  return 'ready';
};
